import PublicHeader from '@/components/PublicHeader'
import PublicFooter from '@/components/PublicFooter'

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-gray-900 dark:text-white transition-colors">
      <PublicHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8 animate-fade-in">
          {/* Hero */}
          <section>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </section>

          {/* Intro */}
          <section className="glass-card rounded-xl p-6 space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              At GistMag, we take your privacy seriously. This Privacy Policy explains what information we
              collect, how we use it, and the choices you have. By using GistMag, you agree to the practices
              described here.
            </p>
          </section>

          {/* Data we collect */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Information We Collect
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>
                <strong>Account information</strong> (such as your name, email address, and password) when you
                sign up.
              </li>
              <li>
                <strong>Billing information</strong> when you subscribe or purchase credits.
                We do not store your full card details.
              </li>
              <li>
                <strong>Usage data</strong> such as API calls, tools used, and credit consumption, so we can
                provide analytics and enforce rate limits.
              </li>
              <li>
                <strong>Image data</strong> that you send to our API for alt text generation. Images are processed
                to generate alt text and are not permanently stored.
              </li>
            </ul>
          </section>

          {/* How we use data */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              How We Use Your Information
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>To provide and maintain the GistMag service and dashboard.</li>
              <li>To generate alt text and related outputs based on the images you submit.</li>
              <li>To manage your subscription, credits, and billing.</li>
              <li>To monitor usage, prevent abuse, and improve performance and reliability.</li>
              <li>
                To communicate with you about important updates, security notifications, and service changes.
              </li>
            </ul>
          </section>

          {/* Data sharing */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              How We Share Your Information
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              We do not sell your personal data. We share information only with trusted third parties that help us
              operate GistMag, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>
                <strong>Payment processors</strong> for secure payment processing and subscription management.
              </li>
              <li>
                <strong>Cloud service providers</strong> for authentication, database storage, and serverless functions.
              </li>
              <li>
                <strong>AI providers</strong> used to process images and generate alt text, under strict
                contractual terms.
              </li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300">
              We may also disclose information if required by law or to protect the rights, property, or safety
              of GistMag, our users, or others.
            </p>
          </section>

          {/* Data retention */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Data Retention
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              We retain your account and usage data for as long as your account is active or as needed to provide
              the service. Image inputs are processed to generate alt text and are not stored longer than
              necessary for processing, logging, and debugging.
            </p>
          </section>

          {/* Security */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Security
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              We use industry-standard security measures to protect your data, including encryption in transit,
              secure authentication, and restricted access to production systems. However, no system is 100% secure,
              and you should protect your API keys and account credentials carefully.
            </p>
          </section>

          {/* Your rights */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Your Rights & Choices
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>Access and update your profile information in the dashboard.</li>
              <li>Rotate or regenerate your API key at any time.</li>
              <li>Cancel your subscription through your account settings or by contacting support.</li>
              <li>Contact us to request account deletion and data removal.</li>
            </ul>
          </section>

          {/* Contact */}
          <section className="glass-card rounded-xl p-6 space-y-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Contact Us
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              If you have any questions about this Privacy Policy or how we handle your data, please contact us at
              <span className="font-semibold"> support@gistmag.co.uk</span>.
            </p>
          </section>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}


